import React, { useState, useEffect } from 'react';

const TrackOrderModal = ({ onClose, restaurantId, onPayClick }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchOrderStatuses = async () => {
        // Read local storage
        const activeOrdersStr = localStorage.getItem('smart_menu_active_orders');
        if (!activeOrdersStr) {
            setOrders([]);
            setLoading(false);
            return;
        }

        let activeOrders = [];
        try {
            activeOrders = JSON.parse(activeOrdersStr);
        } catch (e) {
            console.error("Error parsing local storage orders");
        }

        // Filter for current restaurant
        const currentRestOrders = activeOrders.filter(o => o.restaurant_id === restaurantId);
        
        if (currentRestOrders.length === 0) {
            setOrders([]);
            setLoading(false);
            return;
        }

        const orderIds = currentRestOrders.map(o => o.id).join(',');

        try {
            const res = await fetch(`https://menu.searchisend.in/apimenu/track_order.php?order_ids=${orderIds}`);
            const data = await res.json();
            if (data.status === 'success') {
                setOrders(data.orders);
                
                // Cleanup delivered/cancelled from localStorage
                const terminalStatuses = ['Served', 'Delivered', 'Cancelled', 'Completed', 'Rejected'];
                let hasChanges = false;
                
                const updatedStorage = activeOrders.filter(lsOrder => {
                    if (lsOrder.restaurant_id !== restaurantId) return true; // keep other restaurants
                    
                    const serverOrder = data.orders.find(o => o.id === lsOrder.id);
                    if (serverOrder) {
                        let statusToClear = serverOrder.kitchen_status;
                        if (serverOrder.order_status === 'Cancelled') statusToClear = 'Cancelled';
                        if (serverOrder.order_type === 'Delivery' && serverOrder.delivery_status) {
                            statusToClear = serverOrder.delivery_status;
                        }

                        if (terminalStatuses.includes(statusToClear)) {
                            hasChanges = true;
                            return false; // remove from LS
                        }
                    }
                    return true;
                });

                if (hasChanges) {
                    localStorage.setItem('smart_menu_active_orders', JSON.stringify(updatedStorage));
                }
            }
        } catch (err) {
            console.error("Failed to fetch order status", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrderStatuses();
        const interval = setInterval(fetchOrderStatuses, 15000); // Poll every 15 seconds
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [restaurantId]);

    const getDisplayStatus = (order) => {
        if (order.order_status === 'Awaiting Payment') {
            return 'Awaiting Payment';
        }
        if (order.order_status === 'Cancelled' || order.order_status === 'Rejected') {
            return 'Cancelled';
        }
        if (order.order_type === 'Delivery') {
            if (order.delivery_status === 'Delivered') return 'Delivered';
            if (order.delivery_status === 'Out for Delivery') return 'Out for Delivery';
        }
        return order.kitchen_status || 'Pending';
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'Awaiting Payment': return '#8b5cf6'; // purple
            case 'Pending': return '#f59e0b'; // orange
            case 'Preparing': return '#3b82f6'; // blue
            case 'Ready': return '#10b981'; // green
            case 'Out for Delivery': return '#0ea5e9'; // light blue
            case 'Served':
            case 'Delivered':
            case 'Completed': return '#64748b'; // gray
            case 'Cancelled':
            case 'Rejected': return '#ef4444'; // red
            default: return '#6b7280';
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'Awaiting Payment': return 'fa-wallet';
            case 'Pending': return 'fa-clock';
            case 'Preparing': return 'fa-fire';
            case 'Ready': return 'fa-bell';
            case 'Out for Delivery': return 'fa-motorcycle';
            case 'Served':
            case 'Delivered':
            case 'Completed': return 'fa-check-circle';
            case 'Cancelled':
            case 'Rejected': return 'fa-times-circle';
            default: return 'fa-circle';
        }
    };

    return (
        <div className="dialog-overlay" onClick={onClose} style={{ zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="dialog-content" onClick={(e) => e.stopPropagation()} style={{ background: 'var(--card-bg-color)', borderRadius: '16px', padding: '0', maxWidth: '450px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'stretch', overflow: 'hidden', boxShadow: 'var(--box-shadow-dark)' }}>
                <div style={{ padding: '20px', width: '100%', boxSizing: 'border-box', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: 'var(--text-color-headings)' }}>
                        <i className="fas fa-route" style={{ color: 'var(--primary-orange)', marginRight: '8px' }}></i>
                        Track Your Orders
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: 'var(--text-color-secondary)', cursor: 'pointer' }}>&times;</button>
                </div>

                <div style={{ padding: '20px', width: '100%', boxSizing: 'border-box', overflowY: 'auto', flexGrow: 1, backgroundColor: 'transparent' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-color-secondary)' }}>
                            <i className="fas fa-circle-notch fa-spin fa-2x"></i>
                            <p style={{ marginTop: '10px' }}>Loading status...</p>
                        </div>
                    ) : orders.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-color-secondary)' }}>
                            <i className="fas fa-utensils fa-3x" style={{ opacity: 0.2, marginBottom: '15px' }}></i>
                            <p>No active orders found.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {orders.map(order => (
                                <div key={order.id} style={{ background: 'var(--card-bg-color)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '16px', boxShadow: 'var(--box-shadow-light)' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
                                        <div>
                                            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-color-secondary)', fontWeight: '600' }}>ORDER NO.</p>
                                            <p style={{ margin: '2px 0 0 0', fontSize: '1.1rem', fontWeight: '700', color: 'var(--text-color-headings)' }}>{order.order_no}</p>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-color-secondary)', fontWeight: '600' }}>TOTAL</p>
                                            <p style={{ margin: '2px 0 0 0', fontSize: '1.05rem', fontWeight: '700', color: 'var(--primary-orange)' }}>₹{parseFloat(order.grand_total).toFixed(2)}</p>
                                        </div>
                                    </div>

                                    <div style={{ background: 'var(--bg-color)', borderRadius: '8px', padding: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: `${getStatusColor(getDisplayStatus(order))}20`, color: getStatusColor(getDisplayStatus(order)), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                                            <i className={`fas ${getStatusIcon(getDisplayStatus(order))}`}></i>
                                        </div>
                                        <div>
                                            <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: 'var(--text-color-headings)' }}>
                                                {getDisplayStatus(order)}
                                            </p>
                                            <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: 'var(--text-color-secondary)' }}>
                                                {getDisplayStatus(order) === 'Awaiting Payment' && "Please complete your payment to proceed."}
                                                {getDisplayStatus(order) === 'Pending' && "Waiting for kitchen to accept..."}
                                                {getDisplayStatus(order) === 'Preparing' && "Your food is being prepared."}
                                                {getDisplayStatus(order) === 'Ready' && "Your order is ready to serve/deliver!"}
                                                {getDisplayStatus(order) === 'Out for Delivery' && "Rider is on the way with your order!"}
                                                {(getDisplayStatus(order) === 'Served' || getDisplayStatus(order) === 'Delivered' || getDisplayStatus(order) === 'Completed') && "Order completed."}
                                                {getDisplayStatus(order) === 'Cancelled' && "Order was cancelled."}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {getDisplayStatus(order) === 'Awaiting Payment' && onPayClick && (
                                        <div style={{ marginTop: '12px' }}>
                                            <button 
                                                onClick={() => onPayClick(order)}
                                                style={{ width: '100%', padding: '10px', background: 'var(--primary-orange)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                                            >
                                                <i className="fas fa-qrcode"></i> Pay Now
                                            </button>
                                        </div>
                                    )}

                                    <div style={{ marginTop: '12px', fontSize: '0.75rem', color: 'var(--text-color-secondary)', textAlign: 'center' }}>
                                        Placed at {new Date(order.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrackOrderModal;
